import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError, eur, deDate } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { ArrowLeft, Download, Eye, Printer, Lock, ImageIcon } from "lucide-react";

const Row = ({ label, value, strong }) => (
  <div className="flex justify-between flex-wrap gap-x-6 py-1.5 text-sm">
    <span className={`min-w-0 ${strong ? "text-zinc-900 font-medium" : "text-zinc-500"}`}>{label}</span>
    <span className={`font-mono text-right ml-auto ${strong ? "font-semibold text-zinc-900" : "text-zinc-800"}`}>{value}</span>
  </div>
);

const Card = ({ title, testid, children }) => (
  <div className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]" data-testid={testid}>
    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-2">{title}</div>
    <div className="divide-y divide-zinc-50">{children}</div>
  </div>
);

export default function Buchhaltungsbeleg() {
  const { saleId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/sales/${saleId}/beleg`).then((r) => setData(r.data))
      .catch((e) => setErr(apiError(e, "Beleg konnte nicht geladen werden")));
  }, [saleId]);

  const fetchPdf = async (open) => {
    try {
      const { data: blob } = await api.get(`/sales/${saleId}/gewinn-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      if (open) {
        window.open(url, "_blank");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `Buchhaltungsbeleg-${data?.sale?.internal_number || data?.sale?.number || ""}.pdf`;
        a.click();
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  if (err) return <div className="text-sm text-red-600" data-testid="beleg-error">{err}</div>;
  if (!data) return <div className="text-zinc-400 font-mono text-xs" data-testid="beleg-loading">Laden…</div>;

  const { sale, invoice, tenant, items } = data;
  const profitBefore = Math.round(((sale.profit || 0) - (sale.extra_costs || 0)) * 100) / 100;
  const meta = [
    ["Datum", deDate(sale.date)],
    ["Belegnummer", sale.internal_number || sale.number],
    ["Verkaufsnummer", sale.number],
    ["Kundenrechnung", invoice?.number || "–"],
    ["Kunde", sale.customer_name],
  ];
  const uniq = (arr) => [...new Set(arr.filter(Boolean))];
  const remaining = [...new Set(items.map((i) => i.remaining).filter((r) => r !== null && r !== undefined))];
  const addrLine = [tenant.address, `${tenant.zip} ${tenant.city}`.trim()].filter(Boolean).join(" · ");
  const idLine = [tenant.ust_id ? `USt-IdNr.: ${tenant.ust_id}` : "", tenant.tax_number ? `St.-Nr.: ${tenant.tax_number}` : ""].filter(Boolean).join(" · ");

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="beleg-page">
      <div className="flex items-center justify-between flex-wrap gap-2 print:hidden">
        <Link to="/app/verkauf">
          <Button variant="outline" className="min-h-[44px]" data-testid="beleg-back"><ArrowLeft className="w-4 h-4 mr-1" /> Zurück</Button>
        </Link>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => window.print()} className="min-h-[44px]" data-testid="beleg-print"><Printer className="w-4 h-4 mr-1" /> Drucken</Button>
          <Button variant="outline" onClick={() => fetchPdf(true)} className="min-h-[44px]" data-testid="beleg-pdf-open"><Eye className="w-4 h-4 mr-1" /> PDF ansehen</Button>
          <Button onClick={() => fetchPdf(false)} className="bg-zinc-900 hover:bg-zinc-800 text-white min-h-[44px]" data-testid="beleg-pdf-download"><Download className="w-4 h-4 mr-1" /> PDF herunterladen</Button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm p-5 sm:p-10 space-y-8 print:border-0 print:shadow-none print:rounded-none print:p-0" data-testid="beleg-document">
        {/* Kopfbereich */}
        <div className="space-y-6">
          <div className="flex justify-between gap-8 flex-wrap">
            <div>
              <div className="font-display text-2xl font-bold tracking-tight text-zinc-900" data-testid="beleg-company">{tenant.company_name}</div>
              <div className="text-xs text-zinc-500 mt-2 space-y-0.5">
                {addrLine && <div>{addrLine}</div>}
                {(tenant.email || tenant.phone) && <div>{[tenant.email, tenant.phone].filter(Boolean).join(" · ")}</div>}
                {idLine && <div>{idLine}</div>}
              </div>
            </div>
            <div className="min-w-[230px]" data-testid="beleg-meta">
              {meta.map(([l, v]) => (
                <div key={l} className="flex justify-between gap-6 py-1">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 self-center">{l}</span>
                  <span className="font-mono text-xs font-medium text-zinc-900 text-right">{v}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="border-t border-zinc-200" />
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900" data-testid="beleg-title">Buchhaltungsbeleg</h1>
            <span className="bg-zinc-900 text-white text-[10px] font-mono px-3 py-1.5 rounded-full" data-testid="beleg-intern-badge">INTERN – NICHT FÜR KUNDEN</span>
          </div>
        </div>

        {/* Artikelkarten */}
        <div className="space-y-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">Artikel · {items.length} Position(en)</div>
          {items.map((it, i) => (
            <div key={i} className="bg-white border border-zinc-200 rounded-2xl p-4 sm:p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)] flex gap-4" data-testid={`beleg-article-${i}`}>
              {it.photo ? (
                <img src={it.photo} alt={it.name} className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-xl border border-zinc-200 shrink-0" data-testid={`beleg-article-photo-${i}`} />
              ) : (
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-zinc-100 border border-zinc-200 flex items-center justify-center shrink-0">
                  <ImageIcon className="w-6 h-6 text-zinc-300" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-zinc-900">{it.name}</div>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 border border-zinc-200">{it.number}</span>
                  <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 border border-zinc-200">Ankauf {it.purchase_number || "–"}</span>
                  <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 border border-zinc-200">EK-Datum {deDate(it.purchase_date)}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <div><div className="text-[9px] font-mono uppercase tracking-wider text-zinc-400">Einkaufspreis</div><div className="font-mono text-sm font-medium text-zinc-900 mt-0.5">{eur(it.purchase_price)}</div></div>
                  <div><div className="text-[9px] font-mono uppercase tracking-wider text-zinc-400">Verkaufspreis</div><div className="font-mono text-sm font-medium text-zinc-900 mt-0.5">{eur(it.price)}</div></div>
                  <div><div className="text-[9px] font-mono uppercase tracking-wider text-zinc-400">Menge</div><div className="font-mono text-sm font-medium text-zinc-900 mt-0.5">1 Stück</div></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Finanzkarten */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
          <Card title="Einkauf" testid="beleg-einkauf-card">
            <Row label="Einkaufspreis (steuerlich, gesamt)" value={eur(sale.purchase_cost)} />
            <Row label="Einkaufsdatum" value={uniq(items.map((i) => i.purchase_date)).map(deDate).join(", ") || "–"} />
            <Row label="Verkäufer des Ankaufs" value={uniq(items.map((i) => i.seller)).join(", ") || "–"} />
            <Row label="Verknüpfte Ankäufe" value={uniq(items.map((i) => i.purchase_number)).join(", ") || "–"} />
          </Card>
          <Card title="Verkauf" testid="beleg-verkauf-card">
            <Row label="Verkaufserlös gesamt" value={eur(sale.total)} />
            <Row label="Verkaufte Menge" value={`${items.length} Stück`} />
            <Row label="Verbleibender Bestand" value={remaining.length ? remaining.join(", ") : "–"} />
            <Row label="Verkaufsdatum" value={deDate(sale.date)} />
            <Row label="Mitarbeiter" value={sale.created_by_name || "–"} />
          </Card>
          <Card title="Zusätzliche Kosten" testid="beleg-kosten-card">
            <Row label="Versandkosten" value={eur(sale.shipping || 0)} />
            {(sale.costs || []).map((c, i) => <Row key={i} label={c.label} value={eur(c.amount)} />)}
            <Row label="Zusatzkosten gesamt" value={eur(sale.extra_costs || 0)} strong />
          </Card>
          <Card title="Steuer" testid="beleg-steuer-card">
            <Row label="Angewendete Steuerart" value={sale.tax_label || sale.tax_mode} />
            <Row label="Steuerliche Bemessungsgrundlage" value={eur(sale.net)} />
            <Row label="Steuerbetrag" value={eur(sale.tax)} />
          </Card>
        </div>

        {/* Gewinn */}
        <div className="space-y-4">
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">Gewinn</div>
          <div className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]" data-testid="beleg-gewinn-breakdown">
            <Row label="Verkaufserlös (VK)" value={eur(sale.total)} />
            <Row label="Einkaufskosten (EK)" value={`− ${eur(sale.purchase_cost)}`} />
            <Row label="Zusatzkosten" value={`− ${eur(sale.extra_costs || 0)}`} />
            <div className="border-t border-zinc-100 my-1" />
            <Row label="Gewinn vor Steuer" value={eur(profitBefore)} strong />
            <Row label={`Steuer (${sale.tax_label || sale.tax_mode})`} value={`− ${eur(sale.tax)}`} />
          </div>
          <div className="bg-emerald-50/80 border border-emerald-200 rounded-2xl p-6 sm:p-8 flex items-center justify-between gap-4 flex-wrap" data-testid="beleg-gewinn-nach-steuer">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Gewinn nach Steuer</div>
              <div className="text-xs text-zinc-500 mt-1">VK − EK − Zusatzkosten − Steuer</div>
            </div>
            <div className="font-mono text-4xl sm:text-5xl font-extrabold tracking-tight text-emerald-600" data-testid="beleg-gewinn-nach-steuer-wert">
              {eur(sale.profit_after_tax ?? sale.profit)}
            </div>
          </div>
        </div>

        {/* Interner Hinweis */}
        <div className="bg-zinc-100 border border-zinc-200 rounded-2xl p-4 flex gap-3 items-start" data-testid="beleg-notice">
          <Lock className="w-4 h-4 text-zinc-400 mt-0.5 shrink-0" />
          <div className="text-xs text-zinc-500">
            <span className="font-semibold text-zinc-600">Internes Dokument.</span> Nur für Buchhaltung und Steuerberater.
            Enthält Einkaufspreise, Margen und steuerliche Bemessungsgrundlagen – nicht für Endkunden bestimmt.
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-zinc-200 pt-4 flex justify-between items-end flex-wrap gap-6 text-xs text-zinc-500" data-testid="beleg-footer">
          <div>{tenant.company_name} · {sale.internal_number || sale.number} · Erstellt mit L19</div>
          <div className="text-right">
            <div className="mb-7">{tenant.city ? `${tenant.city}, ` : ""}{deDate(sale.date)}</div>
            <div className="border-b border-zinc-300 w-48 ml-auto" />
            <div className="mt-1 w-48 ml-auto text-center" data-testid="beleg-signature">Unterschrift Mitarbeiter</div>
          </div>
        </div>
      </div>
    </div>
  );
}
